/**
 * Portal identity types + helpers.
 *
 * SCRUM-113 introduces the post-auth Portal Chooser. The bundle
 * (`.design-incoming/auth-admin-20260522/COMPONENT_API.md` §"Helper
 * types (host)") exports the canonical `PortalId` union + four
 * lookup helpers (`portalHome`, `portalTitle`, `portalDesc`,
 * `portalIcon`). Ported here verbatim so the chooser, the post-auth
 * routing decision, and any future portal switcher share a single
 * source of truth.
 *
 * Strings are hard-coded (no i18n yet) — per COPY.md §i18n notes
 * portal IDs are proper nouns and the titles/descriptions live in
 * the bundle. When i18n lands, swap to provider lookups; the
 * function shapes stay.
 */

export type PortalId = 'PMS' | 'MSA' | 'Admin';

const PORTAL_IDS: readonly PortalId[] = ['PMS', 'MSA', 'Admin'];

/**
 * Type guard for `PortalId` — useful for narrowing arbitrary strings
 * (localStorage reads, URL params) into the branded union.
 */
export function isPortalId(value: unknown): value is PortalId {
  return (
    typeof value === 'string' && (PORTAL_IDS as readonly string[]).includes(value)
  );
}

/**
 * Maps a portal id to its in-app home route. Matches the bundle's
 * `portalHome` helper exactly.
 */
export function portalHome(id: PortalId): string {
  switch (id) {
    case 'PMS':
      return '/pms';
    case 'MSA':
      return '/msa';
    case 'Admin':
      return '/admin';
  }
}

/**
 * Human-readable title for a portal id. "Admin" expands to
 * "Internal admin" per the bundle's COPY.md table; the other two
 * stay as their three-letter acronyms.
 */
export function portalTitle(id: PortalId): string {
  return id === 'Admin' ? 'Internal admin' : id;
}

/**
 * One-line portal description used beneath the title in the chooser
 * tile. Strings are the COPY.md `Tile desc` row values.
 */
export function portalDesc(id: PortalId): string {
  switch (id) {
    case 'PMS':
      return 'Run your property management business';
    case 'MSA':
      return 'Cross-org grouping & owner statements';
    case 'Admin':
      return 'Manage companies, staff, and imports';
  }
}

/**
 * Glyph key for a portal id. The chooser/switcher pass this through
 * to the kit `Icon` component once those glyphs are registered. For
 * now (kit Icon doesn't ship swap/shield/home2 yet), the chooser
 * renders inline SVG fallbacks using this key as a discriminator.
 */
export type PortalIconKey = 'home2' | 'swap' | 'shield';

export function portalIcon(id: PortalId): PortalIconKey {
  switch (id) {
    case 'PMS':
      return 'home2';
    case 'MSA':
      return 'swap';
    case 'Admin':
      return 'shield';
  }
}
